import { min, uniqueBy } from 'lib/array/arrayUtils'
import { getMetresDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'
import { areStringsEqual } from 'lib/string/stringUtils'
import type { ExploreItem } from 'tripPlanner/types/common'
import { getStopCoordinates } from 'tripPlanner/utils/chatItinerary'
import { getBoundsCenter, isCoordinateWithinBounds, type MapBounds } from 'lib/maps/bounds'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import {
  EXPLORE_QUERY_PREPOSITION,
  MIN_USER_RATING_COUNT,
  PLACES_IN_DESTINATION_RADIUS_METRES,
} from './explorePlacesConstants'

/** By id, not name: a Cafes chip should still show all three Starbucks. */
export function mergeExplorePlaces(
  ...lists: Array<Array<ExploreItem> | undefined>
): Array<ExploreItem> {
  return uniqueBy(lists.flatMap((list) => list ?? []), (item) => item.id)
}

function hasEnoughReviews(item: ExploreItem): boolean {
  return (item.userRatingCount ?? 0) >= MIN_USER_RATING_COUNT
}

export function filterPlacesByReviewFloor(items: Array<ExploreItem>): Array<ExploreItem> {
  return items.filter(hasEnoughReviews)
}

/** Google's region is not always a neighbourhood — Paris gives "Paris" — so a match is dropped. */
export function getDisplayRegion(
  region: string | undefined,
  destinationName: string | undefined,
): string | undefined {
  if (!region || !destinationName) return region

  return areStringsEqual(region, destinationName) ? undefined : region
}

/**
 * A count cannot detect a nonsense pairing: beaches for an inland town answer from up the coast,
 * not with nothing. Nearest rather than every, because the request is radius-bounded already and
 * Google ranks the closest match first.
 */
export function isNearestPlaceOutsideDestination(
  items: Array<ExploreItem>,
  destination: GeoPoint | undefined,
): boolean {
  if (!destination || items.length === 0) return false

  const nearestMetres = Math.min(...items.map((item) => getMetresDistanceFromLatLong(
    destination.lat,
    destination.lng,
    item.place.point.lat,
    item.place.point.lng,
  )))

  return nearestMetres > PLACES_IN_DESTINATION_RADIUS_METRES
}

/** Takes formattedAddress, not displayName: Sydney is also a city in Nova Scotia. */
export function buildExploreQuery(categoryLabel: string, place: string): string {
  return `${categoryLabel} ${EXPLORE_QUERY_PREPOSITION} ${place}`
}

interface LocatableStop {
  placeName: string
  stopPlaceDetail?: { lat?: number; lng?: number; formattedAddress?: string }
}

/** Query text and camera both read this, so what a search is named after cannot differ from where it flies. */
export function findStopInView<T extends LocatableStop>(
  stops: ReadonlyArray<T> | undefined,
  bounds: MapBounds | undefined,
): T | undefined {
  if (!bounds || !stops?.length) return undefined

  const centre = getBoundsCenter(bounds)
  const stopsInView = stops.flatMap((stop) => {
    const coordinates = getStopCoordinates(stop)
    return coordinates && isCoordinateWithinBounds(coordinates, bounds) ? [{ stop, coordinates }] : []
  })

  return min(stopsInView, ({ coordinates }) =>
    getMetresDistanceFromLatLong(centre.lat, centre.lng, coordinates.lat, coordinates.lng),
  )?.stop
}

/** For a viewport-driven surface, which has no stop of its own. */
export function findDestinationNameInBounds(
  stops: ReadonlyArray<LocatableStop> | undefined,
  bounds: MapBounds | undefined,
): string | undefined {
  const stopInView = findStopInView(stops, bounds)
  if (!stopInView) return undefined

  return stopInView.stopPlaceDetail?.formattedAddress ?? stopInView.placeName
}
